import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { IconEmail, IconPhone, IconText, IconTimes, IconUnread } from '../../icons/library'
import { IconAnnouncementCall } from '../../icons/announcementCall'
import { ScrollArea, ScrollBar } from '../../layouts/components/scrollArea'
import { PopupDateRangePicker } from '../../components/form/datePicker'
import { DateRange } from 'react-day-picker'
import { pluralize, wrapSetState } from '../../utils'
import { useAppContext, User } from '../../providers/context'
import { useTranslation } from '../../providers/translation'
import { BasicUser } from '../../components/userSelector/users'
import { UserSelector } from '../../components/userSelector/userSelector'
import { useIsMobile } from '../../hooks/hooks'

export type Filters = {
  allSchoolMessages: boolean
  unreadOnly: boolean
  search: string
  searchUsers: BasicUser[]
  onlyType: 'email' | 'text' | 'phone' | 'announcement' | undefined
  range: DateRange | undefined
  showArchived: boolean
  showFlagged: boolean
  thisYearsMessages: boolean
}

export const DEFAULT_FILTERS: Filters = {
  allSchoolMessages: false,
  unreadOnly: false,
  search: '',
  searchUsers: [],
  onlyType: undefined,
  range: undefined,
  showArchived: false,
  showFlagged: false,
  thisYearsMessages: false,
}

interface FiltersProps {
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  filters: Filters
  user: User
  before?: React.ReactNode
  showOnMobile?: boolean
}

export const FiltersRow: React.FC<FiltersProps> = ({ setFilters, filters, user, before, showOnMobile }) => {
  const nonDefaultBesidesSearch = Object.keys(DEFAULT_FILTERS).filter(
    (key) =>
      key !== 'thisYearsMessages' &&
      filters[key as keyof Filters] !== DEFAULT_FILTERS[key as keyof Filters] &&
      key !== 'searchUsers' &&
      key !== 'search'
  ).length

  let nonDefault = nonDefaultBesidesSearch + filters.searchUsers.length + (filters.search ? 1 : 0)

  const { tr } = useTranslation()

  const warningsOpen = `${nonDefault > 0 ? 'h-[24.5px] opacity-100' : 'h-0 opacity-0'} transition-[height,opacity] duration-200 overflow-hidden`

  const isMobile = useIsMobile()
  if (!showOnMobile && isMobile) return null

  return (
    <>
      <ScrollArea
        className={`z-20 flex flex-shrink-0 shadow ${showOnMobile ? '' : '-sm:hidden'}`}
        showArrows={true}
        id="tutorial_filters"
      >
        <div className="flex items-center gap-2 p-[8px]">
          {before}
          {user.type === 'teacher' && user.admin > 0 && (
            <Button
              tooltip="Show all messages at your school, instead of just your messages."
              buttonStyle={filters.allSchoolMessages ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() =>
                setFilters({
                  ...filters,
                  allSchoolMessages: !filters.allSchoolMessages,
                  unreadOnly: false,
                })
              }
            >
              All
            </Button>
          )}
          <Button
            disabled={filters.allSchoolMessages}
            buttonStyle={filters.unreadOnly ? ButtonStyle.selected : ButtonStyle.ghostLight}
            onClick={() => setFilters({ ...filters, unreadOnly: !filters.unreadOnly })}
          >
            <IconUnread />
          </Button>
          <FilterSearch filters={filters} setFilters={setFilters} />
          <div className="flex">
            <Button
              buttonStyle={filters.onlyType === 'email' ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() => setFilters({ ...filters, onlyType: filters.onlyType === 'email' ? undefined : 'email' })}
              shape={ButtonShape.groupStart}
            >
              <IconEmail />
            </Button>
            <Button
              buttonStyle={filters.onlyType === 'text' ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() => setFilters({ ...filters, onlyType: filters.onlyType === 'text' ? undefined : 'text' })}
              shape={ButtonShape.groupMiddle}
            >
              <IconText />
            </Button>
            <Button
              buttonStyle={filters.onlyType === 'phone' ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() => setFilters({ ...filters, onlyType: filters.onlyType === 'phone' ? undefined : 'phone' })}
              shape={ButtonShape.groupMiddle}
            >
              <IconPhone />
            </Button>
            {user.district.massCallingEnabled && (
              <Button
                buttonStyle={filters.onlyType === 'announcement' ? ButtonStyle.selected : ButtonStyle.ghostLight}
                onClick={() =>
                  setFilters({
                    ...filters,
                    onlyType: filters.onlyType === 'announcement' ? undefined : 'announcement',
                  })
                }
                shape={ButtonShape.groupEnd}
              >
                <IconAnnouncementCall />
              </Button>
            )}
          </div>
          <PopupDateRangePicker range={filters.range} onRangeChange={(range) => setFilters({ ...filters, range })}>
            <Button buttonStyle={filters.range ? ButtonStyle.selected : ButtonStyle.ghostLight}>
              {filters.range
                ? `${filters.range.from?.toDateString()}-${filters.range.to?.toDateString()}`
                : tr('All Time')}
              {filters.range && (
                <Button
                  useDivAsButton
                  onClick={(e) => {
                    setFilters({ ...filters, range: undefined })
                    e.stopPropagation()
                  }}
                  buttonStyle={ButtonStyle.ghost}
                  shape={ButtonShape.iconSmall}
                  className="-mr-2 ml-2"
                >
                  <IconTimes />
                </Button>
              )}
            </Button>
          </PopupDateRangePicker>
          {user.type === 'teacher' && user.admin > 0 && (
            <Button
              buttonStyle={filters.showArchived ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() => setFilters({ ...filters, showArchived: !filters.showArchived })}
            >
              Show Hidden
            </Button>
          )}
          {user.type === 'teacher' && user.admin > 0 && (
            <Button
              buttonStyle={filters.showFlagged ? ButtonStyle.selected : ButtonStyle.ghostLight}
              onClick={() => setFilters({ ...filters, showFlagged: !filters.showFlagged })}
              tooltip="Show only messages that have been flagged for possible bad language."
            >
              Only Flagged Messages
            </Button>
          )}
        </div>
        <ScrollBar orientation="horizontal" className="-mb-0.5 mt-0.5 flex-1 bg-gray-300/30" />
      </ScrollArea>
      <div className={`${warningsOpen} flex flex-shrink-0 items-center justify-between bg-warning pl-3`}>
        <span className="text-nowrap text-sm">
          {filters.searchUsers.length > 0 || filters.search ? (
            <>
              Showing messages{' '}
              {filters.searchUsers.length === 1 ? (
                <>
                  with <b>{filters.searchUsers[0].name}</b>
                </>
              ) : filters.searchUsers.length > 1 ? (
                <>
                  with <b>{filters.searchUsers.length} people</b>
                </>
              ) : null}
              {filters.search && (
                <>
                  that contain <b>"{filters.search}"</b>
                </>
              )}
              {nonDefaultBesidesSearch > 0 && (
                <>
                  {' + '}
                  <b>{nonDefaultBesidesSearch}</b> more {tr(`${pluralize(nonDefaultBesidesSearch, 'filter')}`)}
                </>
              )}
            </>
          ) : (
            nonDefault > 0 && (
              <>
                <b>{nonDefaultBesidesSearch}</b> {tr(`${pluralize(nonDefaultBesidesSearch, 'filter')}`)} active
              </>
            )
          )}
        </span>
        <Button buttonStyle="" onClick={() => setFilters(DEFAULT_FILTERS)} shape={ButtonShape.roundedSmall}>
          {tr('clear')}
        </Button>
      </div>
    </>
  )
}

export const FilterSearch: React.FC<{
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  className?: string
  simplified?: boolean
}> = ({ filters, setFilters, className, simplified }) => {
  const { tr } = useTranslation()
  const { state } = useAppContext()
  return (
    <UserSelector
      hideInputWhenFull={simplified}
      limitUsers={simplified}
      hideSelectedUsersInDropdown
      clearable
      selectedUsers={filters.searchUsers}
      setSelectedUsers={wrapSetState(setFilters, 'searchUsers')}
      className={`min-w-[200px] ${className}`}
      placeholder={tr('Search')}
      controlSearch={[filters.search, (v: string) => setFilters((f) => ({ ...f, search: v }))]}
      url="/frontend_api/getSearchUsers"
      hideUnnecessaryMessages
      noWrapping
      preserveResultsOnEmptySearch={false}
      showTypeIndicators={state.user?.type !== 'student'}
    />
  )
}
